import React, { useEffect, useState, Suspense } from "react";
import { useHistory } from "react-router-dom";
import usePaginationValuesWhere from '../../hooks/usePaginationValuesWhere';
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { GridView, SvgComponent } from 'components';
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import { ItemManager } from "managers";
import { Item } from "models/ECF/Item";
import Select from "react-select";
import { DEFAULT_PAGINATION_VALUES } from "utils";
import SuspenseECF from "components/Suspense/SuspenseECF";
import ItemEdit from "./ItemEdit";
import Exportar from "components/Exportar/Exportar";

const ESTATUS_FILTER_LIST = [{ value: "", label: "Todos"}, ...Item.ESTATUS_LIST];
const INDICADOR_BIEN_O_SERVICIO_FILTER_LIST = [{value: null, label: "Todos"}, ...Item.INDICADOR_BIEN_O_SERVICIO_LIST];
const INDICADOR_FACTURACION_FILTER_LIST =[{value: null, label: "Todos"}, ...Item.INDICADOR_FACTURACION_LIST];

export default function ItemList({ hideLoading, showLoading, isActive }) {
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [list, setList] = useState([]);
  const [idSec, setIdSec] = useState(undefined);
  const [showCanvas, setShowCanvas] = useState(false);
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { handleException } = useExceptionHandler();
  const [showExportar, setShowExportar] = useState(false);
  const { cerrarAlertas } = useMessage();
  const history = useHistory();

  useEffect(() => {
    if (isActive) {
      buscar();
    }
    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where, isActive]);

  const updateList = () => {
    buscar();
  };

  const buscar = () => {
    const whereWithPaginationValues = { ...where, ...pagination };
    ItemManager.listado(whereWithPaginationValues)
      .then((res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        const transformedList = res.Data.map(item => ({
          ...item,
          IndicadorFacturacion: Item.getFacturacionLabel(item.IndicadorFacturacion),
          IndicadorBienOServicio: Item.getIndicadorBienServicioLabel(item.IndicadorBienOServicio),
          Estatus: Item.getEstatusLabel(item.Estatus),
        }));
        setList(transformedList);
      })
      .catch((error) => {
        handleException(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prevstate) => {
      return { ...prevstate, IndiceDePagina: pageNumber - 1 };
    });
    setWhere({ ...where });
  };

  const handleClick = (params) => {
    if (params.IdSec) {
      setIdSec(params.IdSec);
      setShowCanvas(true);
    } else {
      setIdSec(undefined);
      setShowCanvas(true);
    }
  };

  const openExportar = () => {
    setShowExportar(true);
  };


  return (
    <>
      <div style={{ paddingRight: "6px" }}>
        <ListHeader crearNuevo={handleClick} exportar={openExportar} />
      </div>

      {showExportar && (
        <Suspense fallback={<SuspenseECF />}>
          <Exportar
            PROPIEDADES_EXPORTAR={Item.PROPIEDADES_EXPORTAR}
            showExportar={showExportar}
            setShowExportar={setShowExportar}
            showLoading={showLoading}
            hideLoading={hideLoading}
            where={where}
            serialize={true}
            exportarCallBack={ItemManager.exportar}
          ></Exportar>
        </Suspense>
      )}

      {showCanvas && (
        <Suspense fallback={<SuspenseECF />}>
          <ItemEdit
            showCanvas={showCanvas}
            setShowCanvas={setShowCanvas}
            IdSec={idSec}
            hideLoading={hideLoading}
            showLoading={showLoading}
            updateList={updateList}
          />
        </Suspense>
      )}

      <GridView
        pagination={pagination}
        setPagination={setPagination}
        allowPaging={pagination.EsPaginable}
        pageSize={pagination.CantidadPorPagina}
        pageIndex={pagination.IndiceDePagina}
        lastPageNumber={pagination.PaginasTotal}
        dataSource={list}
        onPageIndexChanging={onPageIndexChanging}
        where={where}
        setWhere={setWhere}
      >
        <GridView.Column
          isField
          width="100px"
          showClearFilter={true}
          bodyRenderer={(row) => {
            return (
              <div className="float-right">
                <IconButton onClick={() => handleClick({ IdSec: row.IdSec })}>
                  <ToolTip message="Editar">
                    <div>
                      <SvgComponent
                        iconName="pencil"
                        height="25"
                        width="25"
                      />
                    </div>
                  </ToolTip>
                </IconButton>
              </div>
            );
          }}
        />
        <GridView.Column
          headerText="Id"
          dataField="IdSec"
          sortExpression="IdSec"
          width="110px"
        />
        <GridView.Column
          headerText="Nombre"
          dataField="Nombre"
          sortExpression="Nombre"
          width="250px"
        />
        <GridView.Column
          headerText="Código de Barra"
          dataField="CodigoBarra"
          sortExpression="CodigoBarra"
          width="250px"
        />
        <GridView.Column
          headerText="Descripción"
          dataField="Descripcion"
          sortExpression="Descripcion"
          width="300px"
        />
        <GridView.Column
          headerText="Tipo ITBIS"
          dataField="IndicadorFacturacion"
          sortExpression="IndicadorFacturacion"
          width="200px"
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={INDICADOR_FACTURACION_FILTER_LIST}
                value={INDICADOR_FACTURACION_FILTER_LIST.filter(function (option) {
                  return (
                    option.value === (where.IndicadorFacturacion ?? INDICADOR_FACTURACION_FILTER_LIST[0].value)
                  );
                })}
                name="IndicadorFacturacion"
                onChange={(e) => {
                  setWhere({
                    ...where,
                    IndicadorFacturacion: e.value,
                  });
                }}
              />
            );
          }}
        />
        <GridView.Column
          headerText="Tipo"
          dataField="IndicadorBienOServicio"
          sortExpression="IndicadorBienOServicio"
          width="210px"
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={INDICADOR_BIEN_O_SERVICIO_FILTER_LIST}
                value={INDICADOR_BIEN_O_SERVICIO_FILTER_LIST.filter(function (option) {
                  return (
                    option.value === (where.IndicadorBienOServicio ?? INDICADOR_BIEN_O_SERVICIO_FILTER_LIST[0].value)
                  );
                })}
                name="IndicadorBienOServicio"
                onChange={(e) => {
                  setWhere({
                    ...where,
                    IndicadorBienOServicio: e.value,
                  });
                }}
              />
            );
          }}
        />
        <GridView.Column
          headerText="Precio"
          dataField="PrecioUnitario"
          sortExpression="PrecioUnitario"
          width="150px"
          number={true}
        />
        <GridView.Column
          headerText="Estatus"
          dataField="Estatus"
          sortExpression="Estatus"
          width="175px"
          filterRenderer={() => {
            return (
              <Select
                className="react-select primary"
                options={ESTATUS_FILTER_LIST}
                value={ESTATUS_FILTER_LIST.filter(function (option) {
                  return (
                    option.value === (where.Estatus ?? ESTATUS_FILTER_LIST[0].value)
                  );
                })}
                name="Estatus"
                onChange={(e) =>
                  setWhere({
                    ...where,
                    Estatus: e.value,
                  })
                }
              />
            );
          }}
        />
      </GridView>
    </>
  );
}